import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormikContextType } from 'formik';
import { UIContext } from '../../context/UIContext';
import FormikTextField from '../../components/formikComponents/FormikTextField';
import FormikSelect from '../../components/formikComponents/FormikSelect';
import FormikCheckbox from '../../components/formikComponents/FormikCheckbox';
import { OPTIONAL_RESTRICTED_INDUSTRY_TYPES } from '../../constants';
import useIndustryTypes from '../../helperHooks/useIndustryTypes';
import FormikDatePicker from '../../components/formikComponents/FormikDatePicker';
import FormikPhoneNumber from '../../components/formikComponents/FormikPhoneNumber';
import AddressFormWithGoogleAutocomplete from '../../components/AddressFormWithGoogleAutocomplete';
import businessTypes from '../../helpers/hardcodedBusinessTypes';
import { formatAndTranslateOptionsList } from '../../helpers/helpers';
import trustTypes from '../../helpers/hardcodedTrustTypes';
import trusteeTypes from '../../helpers/hardcodedTrusteeTypes';

const BusinessInformationForm = ({ formik }: { formik: FormikContextType<any> }) => {
  const { t } = useTranslation();
  const { isMobile } = useContext(UIContext);
  const [validMobileNumber, setValidMobileNumber] = useState<boolean>(false);
  const [phoneNumberError, setPhoneNumberError] = useState<string>('');
  const { industryTypesOptions } = useIndustryTypes();

  const [businessTypesList, setBusinessTypesList] = useState(
    formatAndTranslateOptionsList(
      businessTypes[formik?.values?.country]
        ? businessTypes[formik?.values?.country]
        : businessTypes.default,
      t
    )
  );
  const [trusteeTypesList, setTrusteeTypesList] = useState(
    formatAndTranslateOptionsList(trusteeTypes[formik?.values?.country] || [], t)
  );
  const [trustTypesList, setTrustTypesList] = useState(
    formatAndTranslateOptionsList(trustTypes[formik?.values?.country] || [], t)
  );

  useEffect(() => {
    const currentBusinessTypeList = businessTypesList;
    const newBusinessTypeList = formatAndTranslateOptionsList(
      businessTypes[formik?.values?.country]
        ? businessTypes[formik?.values?.country]
        : businessTypes.default,
      t
    );

    if (JSON.stringify(newBusinessTypeList) !== JSON.stringify(currentBusinessTypeList)) {
      setBusinessTypesList(newBusinessTypeList);
      formik.setFieldValue('businessType', '');
    }

    setTrusteeTypesList(
      formatAndTranslateOptionsList(trusteeTypes[formik?.values?.country] || [], t)
    );
    setTrustTypesList(formatAndTranslateOptionsList(trustTypes[formik?.values?.country] || [], t));
  }, [formik?.values?.country]);

  return (
    <Box sx={{ width: '100%' }}>
      <>
        <FormikTextField
          label={t('screens.businessInformation.businessName')}
          id="organisationName"
          name="organisationName"
          formName="wizardBusinessInformation"
          required
        />
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            gap: isMobile ? 0 : '1rem',
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <FormikSelect
            label={t('screens.businessInformation.industryType')}
            id="industryType"
            name="industryType"
            formName="wizardBusinessInformation"
            required
            options={industryTypesOptions}
            loadingText="screens.businessInformation.loadingIndustryTypes"
          />
          <FormikDatePicker
            label={t('screens.businessInformation.startTradingDate')}
            id="startTradingDate"
            name="startTradingDate"
            formName="wizardBusinessInformation"
            required
            type="text"
            fieldSx={{ width: '100%' }}
            disablePast
          />
        </Box>
        {OPTIONAL_RESTRICTED_INDUSTRY_TYPES?.includes(
          parseInt(formik?.values?.industryType, 10)
        ) && (
          <Box sx={{ marginBottom: '1rem', marginTop: '-1rem' }}>
            <FormikCheckbox
              label={t('screens.businessInformation.proofOfLicenseNeeded')}
              id="proofOfLicenseNeeded"
              name="proofOfLicenseNeeded"
              formName="wizardBusinessInformation"
              type="checkbox"
            />
          </Box>
        )}
        <Typography variant="h3" marginBottom="1rem">
          {t('screens.businessInformation.businessPhoneNumber')}
        </Typography>
        <FormikPhoneNumber
          label={t('screens.businessInformation.businessPhoneNumber')}
          id="phoneNumber"
          name="phoneNumber"
          formName="wizardBusinessInformation"
          required
          type="tel"
          autoComplete="tel"
          error={phoneNumberError}
          clearError={() => {
            setPhoneNumberError('');
          }}
          setError={() => setPhoneNumberError(t('validation.invalidPhoneNumber'))}
          valid={validMobileNumber}
          setValid={setValidMobileNumber}
          defaultCountry={
            formik?.values?.country && formik?.values?.country?.length === 2
              ? formik?.values?.country?.toLowerCase()
              : 'gb'
          }
        />
        <Typography variant="h3" marginBottom="1rem">
          {t('screens.businessInformation.registeredBillingAddress')}
        </Typography>
        <AddressFormWithGoogleAutocomplete
          formik={formik}
          formName="wizardBusinessInformation"
          translationKey="screens.businessInformation.address"
        />
        {formik?.values?.country && (
          <FormikSelect
            label={t('screens.businessInformation.businessType')}
            id="businessType"
            name="businessType"
            formName="wizardBusinessInformation"
            required
            options={businessTypesList}
            loadingText="screens.businessInformation.loadingBusinessTypes"
            renderOption={(props, option) => (
              <li
                {...props}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}
              >
                <Typography variant="body1">{option.label}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ marginLeft: '0.5rem' }}>
                  {option.description}
                </Typography>
              </li>
            )}
          />
        )}
        {formik?.values?.businessType === 10 && (
          <>
            <FormikSelect
              label={t('screens.businessInformation.trusteeType')}
              id="trusteeType"
              name="trust.trusteeType"
              formName="wizardBusinessInformation"
              required
              options={trusteeTypesList}
            />

            {formik?.values?.trust?.trusteeType === 1 && (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: isMobile ? 0 : '1rem',
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <FormikTextField
                  label={t('screens.personalInformation.firstName')}
                  id="trusteeFirstName"
                  name="trust.trusteeFirstName"
                  formName="wizardBusinessInformation"
                  required
                />
                <FormikTextField
                  label={t('screens.personalInformation.lastName')}
                  id="trusteeLastName"
                  name="trust.trusteeLastName"
                  formName="wizardBusinessInformation"
                  required
                />
              </Box>
            )}
            {formik?.values?.trust?.trusteeType === 2 && (
              <FormikTextField
                label={t('screens.businessInformation.trusteeLegalName')}
                id="trusteeLegalName"
                name="trust.trusteeLegalName"
                formName="wizardBusinessInformation"
                required
              />
            )}

            <FormikTextField
              label={t('screens.businessInformation.trustName')}
              id="trustName"
              name="trust.name"
              formName="wizardBusinessInformation"
              required
            />
            <FormikSelect
              label={t('screens.businessInformation.trustType')}
              id="trustType"
              name="trust.type"
              formName="wizardBusinessInformation"
              required
              options={trustTypesList}
            />
          </>
        )}
      </>
    </Box>
  );
};

export default BusinessInformationForm;
